import React, { useEffect, useMemo, useState } from 'react'
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  message
} from 'antd'
import { DollarOutlined, LogoutOutlined, ToolOutlined, FileTextOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import type { Vehicle, VehicleAdjustment, RentAddition } from '../types'
import { matchVehicle } from './vehicleManagerUtils'

const { Text, Title } = Typography
const { Option } = Select

type AdjustmentFormValues = {
  vehicleId?: string
  amount?: number
  note?: string
  effectiveDate?: string
}

const paidStatuses = new Set(['cashier_done', 'accounted', 'completed'])

const PartsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { vehicles, fetchVehicles } = useVehicleStore()

  const [loading, setLoading] = useState(true)
  const [receipts, setReceipts] = useState<any[]>([])
  const [adjustments, setAdjustments] = useState<VehicleAdjustment[]>([])
  const [rentAdditions, setRentAdditions] = useState<RentAddition[]>([])
  const [detailVehicle, setDetailVehicle] = useState<Vehicle | null>(null)
  const [detailVisible, setDetailVisible] = useState(false)
  const [adjustVisible, setAdjustVisible] = useState(false)
  const [adjustType, setAdjustType] = useState<'parts' | 'repair' | 'rent_addition'>('parts')
  const [adjustVehicle, setAdjustVehicle] = useState<Vehicle | null>(null)
  const [editingRecord, setEditingRecord] = useState<VehicleAdjustment | RentAddition | null>(null)
  const [editingType, setEditingType] = useState<'parts' | 'repair' | 'rent_addition'>('parts')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form] = Form.useForm<AdjustmentFormValues>()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      await fetchVehicles().catch(() => {})
      const [receiptRes, adjustmentRes, rentAddRes] = await Promise.all([
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getVehicleRentAdditions()
      ])
      setReceipts(receiptRes.success && receiptRes.data ? receiptRes.data : [])
      const adjustmentsRaw = adjustmentRes.success && adjustmentRes.data ? adjustmentRes.data : []
      const rentAddRaw = rentAddRes.success && rentAddRes.data ? rentAddRes.data : []
      const rentAddsFromAdjustments = adjustmentsRaw.filter((adj: any) => adj.type === 'rent_addition')
      setAdjustments(adjustmentsRaw.filter((adj: any) => adj.type !== 'rent_addition'))
      setRentAdditions([...rentAddRaw, ...rentAddsFromAdjustments])
    } catch (error) {
      console.error(error)
      message.error('加载失败')
    } finally {
      setLoading(false)
    }
  }

  const getReceiptItems = (receipt: any) => {
    const raw = receipt?.items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
        return []
      }
    }
    return []
  }

  const getVehicleReceiptItems = (vehicle: Vehicle) => {
    const results: any[] = []
    receipts.forEach((receipt) => {
      if (!paidStatuses.has(receipt.status)) return
      const items = getReceiptItems(receipt)
      items.forEach((it: any) => {
        if (matchVehicle(it, vehicle)) {
          results.push({ ...it, _receipt: receipt })
        }
      })
    })
    return results
  }

  const getReceiptAmount = (item: any, receipt?: any) => {
    if (item?.actualAmount != null && !Number.isNaN(Number(item.actualAmount))) {
      return Number(item.actualAmount || 0)
    }
    const received = Number(item.receivedAmount ?? item.verifiedAmount ?? item.expectedAmount ?? item.amount ?? 0) || 0
    if (item.receivedAmount != null || item.verifiedAmount != null) {
      return received
    }
    const raw = receipt || item._receipt
    const actualTotal = Number(raw?.actualAmount ?? 0)
    if (!raw || !actualTotal) return received
    const items = getReceiptItems(raw)
    const totalExpected = items.reduce((sum: number, it: any) => sum + (Number(it.expectedAmount ?? it.amount ?? 0) || 0), 0)
    if (!totalExpected) return received
    const expected = Number(item.expectedAmount ?? item.amount ?? 0) || 0
    if (!expected) return received
    return (actualTotal * expected) / totalExpected
  }

  const getVehicleAdjustments = (vehicle: Vehicle) =>
    adjustments.filter(adj => matchVehicle(adj, vehicle))

  const getVehicleRentAdditions = (vehicle: Vehicle) =>
    rentAdditions.filter(add => matchVehicle(add, vehicle))

  const vehicleSummaries = useMemo(() => {
    return vehicles.map(vehicle => {
      const receiptItems = getVehicleReceiptItems(vehicle)
      const paidTotal = receiptItems.reduce((sum, it) => sum + getReceiptAmount(it, it._receipt), 0)
      const vehicleAdjustments = getVehicleAdjustments(vehicle)
      const partsTotal = vehicleAdjustments
        .filter(adj => adj.type === 'parts')
        .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
      const repairTotal = vehicleAdjustments
        .filter(adj => adj.type === 'repair')
        .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
      const rentAddTotal = getVehicleRentAdditions(vehicle)
        .reduce((sum, add) => sum + (Number(add.amount) || 0), 0)
      const revenue = paidTotal + rentAddTotal - partsTotal - repairTotal
      const adjustmentDelta = rentAddTotal - partsTotal - repairTotal
      return {
        vehicle,
        paidTotal,
        partsTotal,
        repairTotal,
        rentAddTotal,
        revenue,
        adjustmentDelta
      }
    })
  }, [vehicles, receipts, adjustments, rentAdditions])

  const filteredSummaries = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    if (!keyword) return vehicleSummaries
    return vehicleSummaries.filter(({ vehicle }) => {
      const plate = String(vehicle.plate || '').toLowerCase()
      const company = String(vehicle.company || '').toLowerCase()
      const contact1 = String(vehicle.contactPerson || '').toLowerCase()
      const contact2 = String((vehicle as any).contactPerson2 || '').toLowerCase()
      const phone = String(vehicle.phone || '')
      const phone2 = String((vehicle as any).phone2 || '')
      return (
        plate.includes(keyword) ||
        company.includes(keyword) ||
        contact1.includes(keyword) ||
        contact2.includes(keyword) ||
        phone.includes(keyword) ||
        phone2.includes(keyword)
      )
    })
  }, [vehicleSummaries, searchKeyword])

  const handleSearch = (value: string) => {
    setSearchKeyword(value)
    setCurrentPage(1)
  }

  const totalIncome = useMemo(() => {
    const paidTotal = receipts
      .filter(r => paidStatuses.has(r.status))
      .reduce((sum, receipt) => {
        const items = getReceiptItems(receipt)
        return sum + items.reduce((s: number, it: any) => s + getReceiptAmount(it, receipt), 0)
      }, 0)
    const rentAddTotal = rentAdditions
      .reduce((sum, add) => sum + (Number(add.amount) || 0), 0)
    return paidTotal + rentAddTotal
  }, [receipts, rentAdditions])

  const totalParts = useMemo(
    () => adjustments.filter(adj => adj.type === 'parts').reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0),
    [adjustments]
  )
  const totalRepair = useMemo(
    () => adjustments.filter(adj => adj.type === 'repair').reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0),
    [adjustments]
  )

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const openAdjustmentModal = (vehicle: Vehicle | null, type: 'parts' | 'repair' | 'rent_addition') => {
    setAdjustType(type)
    setEditingType(type)
    setAdjustVehicle(vehicle)
    setEditingRecord(null)
    form.resetFields()
    if (vehicle) {
      form.setFieldsValue({ vehicleId: vehicle._id || vehicle.id })
    }
    setAdjustVisible(true)
  }

  const openEditAdjustment = (
    vehicle: Vehicle,
    record: VehicleAdjustment | RentAddition,
    type: 'parts' | 'repair' | 'rent_addition'
  ) => {
    setAdjustType(type)
    setEditingType(type)
    setAdjustVehicle(vehicle)
    setEditingRecord(record)
    form.resetFields()
    form.setFieldsValue({
      vehicleId: vehicle._id || vehicle.id,
      amount: record.amount,
      note: record.note,
      effectiveDate: record.effectiveDate ? dayjs(record.effectiveDate) : undefined
    })
    setAdjustVisible(true)
  }

  const getRecordId = (record: any) => record?._id || record?.id

  const handleDeleteAdjustment = async (record: VehicleAdjustment | RentAddition, type: 'parts' | 'repair' | 'rent_addition') => {
    const recordId = getRecordId(record)
    if (!recordId) {
      message.error('缺少记录ID')
      return
    }
    Modal.confirm({
      title: '确认删除该记录？',
      onOk: async () => {
        const res = type === 'rent_addition'
          ? await simpleDirectCloudApi.deleteVehicleRentAddition(recordId)
          : await simpleDirectCloudApi.deleteVehicleAdjustment(recordId)
        if (!res.success) {
          message.error(res.error || '删除失败')
          return
        }
        message.success('删除成功')
        loadData()
      }
    })
  }

  const handleSaveAdjustment = async () => {
    const values = await form.validateFields()
    const vehicleId = values.vehicleId || adjustVehicle?._id || adjustVehicle?.id
    if (!vehicleId) {
      message.error('请选择车辆')
      return
    }
    const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId || v.plate === vehicleId)
    if (!vehicle) {
      message.error('车辆不存在')
      return
    }
    const amount = Number(values.amount || 0)
    if (!amount || amount <= 0) {
      message.error('请输入有效金额')
      return
    }

    const effectiveDate = values.effectiveDate
      ? dayjs(values.effectiveDate).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')

    const basePayload = {
      vehicleId: vehicle._id || vehicle.id,
      plate: vehicle.plate,
      company: vehicle.company,
      amount,
      note: values.note || '',
      effectiveDate,
      createdAt: new Date().toISOString(),
      createdBy: user?.nickName || ''
    }

    const recordId = editingRecord ? getRecordId(editingRecord) : null
    const res = editingType === 'rent_addition'
      ? (recordId
          ? await simpleDirectCloudApi.updateVehicleRentAddition(recordId, { ...basePayload, type: 'rent_addition' })
          : await simpleDirectCloudApi.addVehicleRentAddition({ ...basePayload, type: 'rent_addition' }))
      : (recordId
          ? await simpleDirectCloudApi.updateVehicleAdjustment(recordId, { ...basePayload, type: editingType } as VehicleAdjustment)
          : await simpleDirectCloudApi.addVehicleAdjustment({ ...basePayload, type: editingType } as VehicleAdjustment))
    if (!res.success) {
      message.error(res.error || '保存失败')
      return
    }
    message.success('保存成功')
    setAdjustVisible(false)
    setAdjustVehicle(null)
    setEditingRecord(null)
    form.resetFields()
    loadData()
  }

  const openDetail = (vehicle: Vehicle) => {
    setDetailVehicle(vehicle)
    setDetailVisible(true)
  }

  const renderReceiptRange = (item: any) => {
    if (item.chargeRangeStart && item.chargeRangeEnd) {
      return `${item.chargeRangeStart} ~ ${item.chargeRangeEnd}`
    }
    const receipt = item._receipt || {}
    return item.periodKey || item.dueDate || receipt.dueDate || '-'
  }

  const detailReceipts = detailVehicle ? getVehicleReceiptItems(detailVehicle) : []
  const detailAdjustments = detailVehicle ? getVehicleAdjustments(detailVehicle) : []
  const detailRentAdditions = detailVehicle ? getVehicleRentAdditions(detailVehicle) : []
  const getLeaseHistory = (vehicle: Vehicle) => {
    const history = Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : []
    return history
      .filter((l: any) => l && (l.company || l.contactPerson || l.startDate))
      .sort((a: any, b: any) => dayjs(b.startDate || 0).valueOf() - dayjs(a.startDate || 0).valueOf())
  }
  const getCurrentLease = (vehicle: Vehicle) => {
    const history = getLeaseHistory(vehicle)
    const currentId = (vehicle as any).currentLeaseId
    if (currentId) {
      const match = history.find((l: any) => l.leaseId === currentId)
      if (match) return match
    }
    return history[0] || null
  }
  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Text>加载中...</Text>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', background: 'var(--bg)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} style={{ backgroundColor: 'var(--warning)' }} icon={<ToolOutlined />} />
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || 'parts'}</Title>
                <Text type="secondary">{user?.info || '配置端'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<FileTextOutlined />} onClick={() => openAdjustmentModal(null, 'rent_addition')}>
                补录租金
              </Button>
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>
                退出
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="累计租金收入"
              value={totalIncome}
              valueStyle={{ color: 'var(--positive)' }}
              prefix={<DollarOutlined />}
              formatter={(v) => `￥${Number(v || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="配件扣款"
              value={totalParts}
              valueStyle={{ color: 'var(--warning)' }}
              formatter={(v) => `￥${Number(v || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="维修扣款"
              value={totalRepair}
              valueStyle={{ color: '#ff7a45' }}
              formatter={(v) => `￥${Number(v || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="净营收"
              value={totalIncome - totalParts - totalRepair}
              valueStyle={{ color: '#1677ff' }}
              formatter={(v) => `￥${Number(v || 0).toFixed(2)}`}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={`车辆收款汇总 (${filteredSummaries.length})`}
        extra={
          <Input.Search
            placeholder="车牌号/公司/联系人/电话..."
            allowClear
            onSearch={handleSearch}
            onChange={(e) => {
              if (!e.target.value) handleSearch('')
            }}
            style={{ width: 260 }}
          />
        }
      >
        <List
          itemLayout="vertical"
          dataSource={filteredSummaries}
          pagination={{
            current: currentPage,
            pageSize,
            total: filteredSummaries.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (page, size) => {
              setCurrentPage(page)
              if (size !== pageSize) {
                setPageSize(size || 10)
                setCurrentPage(1)
              }
            },
            onShowSizeChange: (_, size) => {
              setPageSize(size || 10)
              setCurrentPage(1)
            },
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`
          }}
          renderItem={({ vehicle, paidTotal, partsTotal, repairTotal, rentAddTotal, revenue, adjustmentDelta }) => (
            <List.Item
              key={vehicle._id || vehicle.id || vehicle.plate}
              actions={[
                <Button key="detail" type="link" onClick={() => openDetail(vehicle)}>明细</Button>,
                <Button key="rent" type="link" onClick={() => openAdjustmentModal(vehicle, 'rent_addition')}>补录租金</Button>,
                <Button key="parts" type="link" onClick={() => openAdjustmentModal(vehicle, 'parts')}>配件扣款</Button>,
                <Button key="repair" type="link" onClick={() => openAdjustmentModal(vehicle, 'repair')}>维修扣款</Button>
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                title={
                  <Space>
                    <Text strong>{vehicle.plate}</Text>
                    <Tag color="green">已收租金 ￥{(paidTotal + rentAddTotal).toFixed(2)}</Tag>
                    <Tag color="orange">配件 ￥{partsTotal.toFixed(2)}</Tag>
                    <Tag color="volcano">维修 ￥{repairTotal.toFixed(2)}</Tag>
                    <Tag color={revenue >= 0 ? 'blue' : 'red'}>净营收 ￥{revenue.toFixed(2)}</Tag>
                  </Space>
                }
                description={
                  <div>
                    <Text type="secondary">公司：</Text>
                    <Text>{vehicle.company || '-'}</Text>
                    <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                    <Text>{vehicle.contactPerson || '-'}</Text>
                    <Text type="secondary" style={{ marginLeft: 12 }}>调整净额：</Text>
                    <Text>￥{adjustmentDelta.toFixed(2)}</Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={detailVehicle ? `${detailVehicle.plate} 明细` : '车辆明细'}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
      >
        {detailVehicle && (
          <div>
            <Space style={{ marginBottom: 12 }}>
              <Tag color="blue">公司：{detailVehicle.company || '-'}</Tag>
              <Tag>联系人：{detailVehicle.contactPerson || '-'}</Tag>
              <Tag>租金：￥{detailVehicle.rentAmount || 0}</Tag>
            </Space>

            <Card size="small" title="租客信息" style={{ marginBottom: 12 }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Text strong>当前租客</Text>
                {getCurrentLease(detailVehicle) ? (
                  <Space wrap>
                    <Tag color="gold">公司：{getCurrentLease(detailVehicle)?.company || '-'}</Tag>
                    <Tag>联系人：{getCurrentLease(detailVehicle)?.contactPerson || '-'}</Tag>
                    <Tag>起租：{getCurrentLease(detailVehicle)?.startDate || '-'}</Tag>
                    <Tag>到期：{getCurrentLease(detailVehicle)?.endDate || '-'}</Tag>
                  </Space>
                ) : (
                  <Text type="secondary">暂无当前租客记录</Text>
                )}
                <Text strong>历史租客</Text>
                {getLeaseHistory(detailVehicle).length ? (
                  <List
                    size="small"
                    dataSource={getLeaseHistory(detailVehicle)}
                    renderItem={(lease: any) => (
                      <List.Item>
                        <Space wrap>
                          <Text>{lease.company || '-'}</Text>
                          <Text type="secondary">联系人：{lease.contactPerson || '-'}</Text>
                          <Text type="secondary">起租：{lease.startDate || '-'}</Text>
                          <Text type="secondary">到期：{lease.endDate || '-'}</Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Text type="secondary">暂无历史租客</Text>
                )}
              </Space>
            </Card>

            <Card size="small" title="收款记录" style={{ marginBottom: 12 }}>
              <List
                dataSource={detailReceipts}
                locale={{ emptyText: '暂无收款记录' }}
                renderItem={(item: any) => {
                  const receipt = item._receipt || {}
                  const amount = getReceiptAmount(item)
                  return (
                    <List.Item>
                      <Space direction="vertical" size={0}>
                        <Text>{receipt.company || receipt.customerName || detailVehicle.company || '-'}</Text>
                        <Text type="secondary">区间：{renderReceiptRange(item)}</Text>
                        <Text type="secondary">金额：￥{amount.toFixed(2)}</Text>
                        <Text type="secondary">时间：{receipt.verifiedAt ? dayjs(receipt.verifiedAt).format('YYYY-MM-DD') : (receipt.createdAt ? dayjs(receipt.createdAt).format('YYYY-MM-DD') : '-')}</Text>
                      </Space>
                    </List.Item>
                  )
                }}
              />
            </Card>

            <Card size="small" title="补录租金" style={{ marginBottom: 12 }}>
              <List
                dataSource={detailRentAdditions}
                locale={{ emptyText: '暂无记录' }}
                renderItem={(add: RentAddition) => (
                  <List.Item
                    actions={[
                      <Button key="edit" type="link" onClick={() => detailVehicle && openEditAdjustment(detailVehicle, add, 'rent_addition')}>
                        编辑
                      </Button>,
                      <Button key="delete" type="link" danger onClick={() => handleDeleteAdjustment(add, 'rent_addition')}>
                        删除
                      </Button>
                    ]}
                  >
                    <Space direction="vertical" size={0}>
                      <Text>补录租金：￥{Number(add.amount || 0).toFixed(2)}</Text>
                      <Text type="secondary">日期：{add.effectiveDate || add.createdAt ? dayjs(add.effectiveDate || add.createdAt).format('YYYY-MM-DD') : '-'}</Text>
                      <Text type="secondary">备注：{add.note || '-'}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>

            <Card size="small" title="配件/维修扣款" style={{ marginBottom: 12 }}>
              <List
                dataSource={detailAdjustments}
                locale={{ emptyText: '暂无记录' }}
                renderItem={(adj: VehicleAdjustment) => (
                  <List.Item
                    actions={[
                      <Button
                        key="edit"
                        type="link"
                        onClick={() => detailVehicle && openEditAdjustment(detailVehicle, adj, adj.type as 'parts' | 'repair')}
                      >
                        编辑
                      </Button>,
                      <Button key="delete" type="link" danger onClick={() => handleDeleteAdjustment(adj, adj.type as 'parts' | 'repair')}>
                        删除
                      </Button>
                    ]}
                  >
                    <Space direction="vertical" size={0}>
                      <Text>
                        {adj.type === 'parts' ? '配件扣款' : '维修扣款'}：
                        ￥{Number(adj.amount || 0).toFixed(2)}
                      </Text>
                      <Text type="secondary">日期：{adj.effectiveDate || adj.createdAt ? dayjs(adj.effectiveDate || adj.createdAt).format('YYYY-MM-DD') : '-'}</Text>
                      <Text type="secondary">备注：{adj.note || '-'}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )}
      </Modal>

      <Modal
        title={editingType === 'rent_addition' ? '补录租金' : editingType === 'parts' ? '配件扣款' : '维修扣款'}
        open={adjustVisible}
        onCancel={() => setAdjustVisible(false)}
        onOk={handleSaveAdjustment}
        okText="保存"
      >
        <Form form={form} layout="vertical">
          {!adjustVehicle && (
            <Form.Item name="vehicleId" label="选择车辆" rules={[{ required: true, message: '请选择车辆' }]}>
              <Select
                placeholder="选择车辆"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {vehicles.map(v => (
                  <Option key={v._id || v.id} value={v._id || v.id}>
                    {v.plate} - {v.company}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="effectiveDate" label="发生日期">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default PartsPage
